
import { useState, useEffect } from "react";
import { 
  CONSENT_COOKIE_NAME, 
  LANGUAGE_COOKIE_NAME,
  CookieConsent,
  defaultConsent,
  detectBrowserLanguage,
  getUserLanguage,
  saveUserLanguage
} from "@/lib/i18n";
import { SupportedLanguage } from "@/components/multilingual/LanguageSelector";
import CookieBannerAlert from "./CookieBannerAlert";
import CookieSettingsDialog from "./CookieSettingsDialog";

export function CookieBannerMain() {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [language, setLanguage] = useState<SupportedLanguage>(getUserLanguage());
  const [consent, setConsent] = useState<CookieConsent>({...defaultConsent});
  
  // Initialize language and check for consent
  useEffect(() => {
    // Check if consent was already given
    const savedConsent = localStorage.getItem(CONSENT_COOKIE_NAME);
    if (!savedConsent) {
      setShowBanner(true);
    } else {
      try {
        // Parse saved consent
        const parsedConsent = JSON.parse(savedConsent);
        setConsent(parsedConsent);
      } catch (error) {
        // If parsing fails, reset consent and show banner
        setShowBanner(true);
      }
    }
  }, []);

  // Handle language change
  const handleLanguageChange = (value: SupportedLanguage) => {
    setLanguage(value);
    saveUserLanguage(value);
  };
  
  // Save consent with given preferences
  const saveConsent = (consentData: CookieConsent) => {
    localStorage.setItem(CONSENT_COOKIE_NAME, JSON.stringify(consentData));
    setConsent(consentData);
    setShowBanner(false);
    setShowSettings(false);
  };
  
  // Accept all cookies
  const acceptAll = () => {
    const fullConsent: CookieConsent = {
      necessary: true,
      analytics: true,
      marketing: true,
      preferences: true
    };
    saveConsent(fullConsent);
  };
  
  // Accept only essential cookies
  const acceptEssential = () => {
    saveConsent({...defaultConsent});
  };
  
  // Toggle settings dialog
  const toggleSettings = () => {
    setShowSettings(!showSettings);
  };
  
  // TDDDG § 25 compliant: Record explicit rejection when dismissing
  const dismiss = () => {
    const rejectionConsent: CookieConsent = {
      necessary: true,
      analytics: false,
      marketing: false,
      preferences: false
    };
    saveConsent(rejectionConsent);
  };
  
  // Handle switch changes in the settings
  const handleSwitchChange = (category: keyof CookieConsent) => {
    if (category === 'necessary') return; // Can't toggle necessary cookies
    
    setConsent(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };
  
  if (!showBanner) return null;
  
  return (
    <>
      <CookieBannerAlert 
        language={language}
        onLanguageChange={handleLanguageChange}
        onAcceptAll={acceptAll}
        onAcceptEssential={acceptEssential}
        onOpenSettings={toggleSettings}
        onDismiss={dismiss}
      />

      <CookieSettingsDialog
        open={showSettings}
        onOpenChange={setShowSettings}
        language={language}
        consent={consent}
        onSwitchChange={handleSwitchChange}
        onSave={() => saveConsent(consent)}
        onClose={() => setShowSettings(false)}
      />
    </>
  );
}

export default CookieBannerMain;
